import { PureComponent } from "react";
import { withGenericConfig } from "/contexts/generic-config-context";
import { getContactFormConfigById } from "client/services/hubspot-service/contact-form-config";
import {
  HUBSPOT_URL_PARAM_LIST,
  HUBSPOT_API_VISITOR,
  HUBSPOT_API_CLUSTER,
  HUBSPOT_API_FROM,
  HUBSPOT_API_SOURCE,
  HUBSPOT_API_PRIVACY_CONSENT,
  URL_PARAM_FROM
} from "client/services/hubspot-service/hubspot-constants";
import {
  getListOfUrlParamsToHubspotForm,
  getSingleUrlParamToHubspotForm,
  sendHubspotForm
} from "client/services/hubspot-service/hubspot-service";
import { getCookie } from "/services/cookies-service";
import {
  HUBSPOT_FORM_SUBMIT_CLICKED,
  HUBSPOT_FORM_SUBMIT_FAILED,
  HUBSPOT_FORM_READY,
  HUBSPOT_FORM_INPUT_BLUR
} from "constants/bigbrain-event-types";
import { BIG_BRAIN_VISITOR_ID, CLUSTER_COOKIE } from "/constants/cookies";
import DataLayerEventTypes from "constants/data-layer-event-types";
import { fireGtmEvent } from "client/services/gtm-service/gtm-service";
import { IPO_CAMPAIGN_ID } from "constants/sales/campaigns";
import BaseForm from "segments/desktop/core-components/base-form/base-form";
import { componentStyles } from "./hubspot-contact-form.scss";
import DefaultProps from "./default-props";

const HUBSPOT_INVALID_EMAIL_ERROR_TYPE = "INVALID_EMAIL";
const UTM_CAMPAIGN_PARAM_NAME = "utm_campaign";

class HubspotContactForm extends PureComponent {
  componentDidMount() {
    const { formId } = this.props;
    BigBrain("track", HUBSPOT_FORM_READY, { kind: formId });
  }

  generateHiddenData = () => {
    const { withPrivacyNotice, source, overrideCampaignId } = this.props;

    let urlParams = getListOfUrlParamsToHubspotForm(window.location, HUBSPOT_URL_PARAM_LIST);

    if (overrideCampaignId) {
      const urlParamsWithoutCampaign = urlParams.filter((param) => param?.name !== UTM_CAMPAIGN_PARAM_NAME);
      urlParamsWithoutCampaign.push({ name: UTM_CAMPAIGN_PARAM_NAME, value: overrideCampaignId });
      urlParams = urlParamsWithoutCampaign;
    }

    const hiddenData = [...urlParams];

    const visitorId = getCookie(BIG_BRAIN_VISITOR_ID) || "";
    hiddenData.push({ name: HUBSPOT_API_VISITOR, value: visitorId });

    const clusterId = getCookie(CLUSTER_COOKIE) || "";
    hiddenData.push({ name: HUBSPOT_API_CLUSTER, value: clusterId });

    hiddenData.push({ name: HUBSPOT_API_SOURCE, value: source });

    hiddenData.push({ name: HUBSPOT_API_PRIVACY_CONSENT, value: withPrivacyNotice });

    const fromUrlParamObject = getSingleUrlParamToHubspotForm(window.location, URL_PARAM_FROM, HUBSPOT_API_FROM);
    if (fromUrlParamObject.value) hiddenData.push(fromUrlParamObject);

    return hiddenData;
  };

  getFirstErrorMessageFromResponse = (response) => {
    if (response?.errors?.[0]?.errorType === HUBSPOT_INVALID_EMAIL_ERROR_TYPE) {
      //Formatting Hubspot message into more user friendly one for known message types
      return "form.errors.invalidEmailError";
    }

    return response?.errors?.[0]?.message;
  };

  sendFormToHubspot = async (formData) => {
    const { formId, portalId } = this.props;

    const hiddenDataParams = this.generateHiddenData();
    const fields = [...formData, ...hiddenDataParams];

    const response = await sendHubspotForm(portalId, formId, fields);
    const success = response && !response.status; // Success request has no status entry
    let errorMessage = null;

    if (!success) {
      const firstErrorType = response?.errors && response?.errors[0]?.errorType;
      errorMessage = this.getFirstErrorMessageFromResponse(response);
      BigBrain("track", HUBSPOT_FORM_SUBMIT_FAILED, { kind: formId, info1: firstErrorType, info2: errorMessage });
    } else {
      BigBrain("track", HUBSPOT_FORM_SUBMIT_CLICKED, { kind: formId });
      fireGtmEvent(DataLayerEventTypes.HUBSPOT_FORM_SUBMITTED, { formId });
    }

    return { success, errorMessage };
  };

  getSubmitScreenData = () => {
    const { overrideCampaignId } = this.props;
    if (overrideCampaignId === IPO_CAMPAIGN_ID) {
      return {
        titleKey: "forms.thanksPage.ipoCampaign.title",
        subTitleHtmlKey: "forms.thanksPage.ipoCampaign.subTitleHtml"
      };
    }

    return null;
  };

  onInputBlur = (name, value) => {
    const { formId } = this.props;
    BigBrain("track", HUBSPOT_FORM_INPUT_BLUR, { kind: formId, info1: name, info2: value.length });
  }

  render() {
    const { withPrivacyNotice, formId, overrideCampaignId, backgroundLayers } = this.props;
    const formConfig = getContactFormConfigById(formId, overrideCampaignId, withPrivacyNotice);
    const submitScreenData = this.getSubmitScreenData();

    return (
      <div className="hubspot-contact-form-wrapper">
        <BaseForm
          backgroundLayers={backgroundLayers}
          formConfig={formConfig}
          submitFormAsyncCallback={this.sendFormToHubspot}
          submitScreenData={submitScreenData}
          onInputBlur={this.onInputBlur}
          renderPrivacyNotice={withPrivacyNotice}
        />
        <style jsx>{componentStyles}</style>
      </div>
    );
  }
}

HubspotContactForm.defaultProps = DefaultProps;

export default withGenericConfig(HubspotContactForm);